import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  TextField,
  Button,
  MenuItem,
  MenuList,
  Paper,
  Alert,
  DialogContent,
  DialogContentText,
  Dialog,
  DialogTitle,
  DialogActions,
  Tooltip,
  IconButton,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Map, { Marker, NavigationControl } from "react-map-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import * as turf from "@turf/turf";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import DrawCircle from "./components/DrawCircle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import { priceList } from "./apis/apisList/adminApi";
import { editProject } from "./apis/apisList/userApi";
import { useApp } from "./Context/AppContext";
import MapSidebar from "./components/MapSidebar";
import UploadKMLDialog from "./components/KMLUpload";
import moment from "moment";
import MarkerComponent from "./components/MarkerComponent";
import InstructionTooltip from "./components/InstructionTooltip";
import planConfig from "./config/planConfig";
import axios from "axios";
import InstructionOverlay from "./components/InstructionPopup";
import { Download } from "@mui/icons-material";
import { generateKML } from "./lib/generateKml";
import useIpAddress from "./lib/ipAddressHook";
import { downloadKmlApi } from "./apis/apisList/commonApi";
import { LoadingButton } from "@mui/lab";
import PolylineIcon from "@mui/icons-material/Polyline";
import CustomMarkerMap from "./components/CustomMarkerMap";
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const getAddressFromCoords = async (cords) => {
  if (!cords?.length) return;
  const [longitude, latitude] = cords;
  if (!latitude || !longitude) return null;
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&limit=1`;

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (data.features.length > 0) {
      return data.features[0].place_name; // Returns the formatted address
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error fetching address:", error);
    return null;
  }
};

const MapWithSidebar = () => {
  // const [selectedOption, setSelectedOption] = useState(null);
  const [markers, setMarkers] = useState([]); // For Photos Plan only
  const [groundControlMarkers, setGroundControlMarkers] = useState([]); // For Ground Control Points Plan
  const [searchQuery, setSearchQuery] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [searchLocation, setSearchLocation] = useState(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const [lineCoords, setLineCoords] = useState([]);
  const [lineStrings, setLineStrings] = useState([]);
  const [videoPlanData, setVideoPlanData] = useState([]);
  const [videoPlanLine, setVideoPlanLine] = useState([]);
  const [roundedArea, setRoundedArea] = useState(null);
  const {
    projectData,
    setProjectData,
    selectedOption,
    setSelectedOption,
    currentStep,
    setCurrentStep,
    prName,
    setPrName,
  } = useApp();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();
  const [showAlert, setShowAlert] = useState(false);
  const [interactive, setInteractive] = useState(true); // Controls map interaction
  const videoSelectRef = useRef(null);
  const grammeSelectRef = useRef(null);
  const lidarSelectRef = useRef(null);
  const [priceData, setPriceData] = useState([]);
  const [openMenu, setOpenMenu] = useState(false);
  const [viewState, setViewState] = useState(() => {
    return (
      JSON.parse(localStorage.getItem("current_location")) || {
        longitude: -122.447303,
        latitude: 37.753574,
        zoom: 14,
      }
    );
  });
  const [instruction, setInstruction] = useState("");
  const [openKmlDialog, setOpenKmlDialog] = useState(false);
  const [searchParams] = useSearchParams();
  const [instructionType, setInstructionType] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [kmlData, setKmlData] = useState(null);
  const [isKmlDownloading, setIsKmlDownloading] = useState(false);
  const IP = useIpAddress();
  const [isEdit, setIsEdit] = useState(false);
  const [editInstruction, setEditInstruction] = useState({
    message: "",
    position: {},
    isShow: false,
  });

  // New: selected service metadata for sub-services flow
  const [selectedMainServiceKey, setSelectedMainServiceKey] = useState(null);
  const [selectedMainServiceLabel, setSelectedMainServiceLabel] =
    useState(null);
  const [selectedSubServiceKey, setSelectedSubServiceKey] = useState(null);
  const [selectedSubServiceLabel, setSelectedSubServiceLabel] = useState(null);
  const [isPhotoDraw, setIsPhotoDraw] = useState(() => {
    return !!localStorage.getItem("photo_draw");
  });

  const safeResize = () => {
    try {
      mapRef?.current?.getMap()?.resize();
    } catch {}
  };

  const updateDirection = (markerId, properties) => {
    if (selectedOption === "Photos Plan") {
      const newMarkers = markers.map((m) =>
        m.properties.id === markerId
          ? {
              ...m,
              properties: { ...m.properties, arrowProperties: properties },
            }
          : m
      );
      setMarkers(newMarkers);
    }
    if (
      selectedOption === planConfig?.ground_control?.ground_control_points_plan
    ) {
      const newMarkers = groundControlMarkers.map((m) =>
        m.properties.id === markerId
          ? {
              ...m,
              properties: { ...m.properties, arrowProperties: properties },
            }
          : m
      );
      setGroundControlMarkers(newMarkers);
    }
  };

  // Delete a marker
  const deleteMarker = (maker) => {
    if (selectedOption === "Photos Plan") {
      setMarkers((prevMarkers) =>
        prevMarkers.filter(
          (marker) => marker.properties.id !== maker.properties.id
        )
      );
    }
    if (
      selectedOption === planConfig?.ground_control?.ground_control_points_plan
    ) {
      setGroundControlMarkers((prevMarkers) =>
        prevMarkers.filter(
          (marker) => marker.properties.id !== maker.properties.id
        )
      );
    }
  };

  useEffect(() => {
    const storeData = handleProjectSave();
    if (!storeData) {
      if (projectData.length === 0) {
        setIsReady(false);
        return;
      }
    } else {
      setKmlData(storeData);
      setIsReady(true);
      return;
    }
  }, [
    projectData,
    markers,
    lineCoords,
    lineStrings,
    videoPlanData,
    videoPlanLine,
    roundedArea,
  ]);

  useEffect(() => {
    try {
      if (mapRef.current) {
        const map = mapRef.current.getMap();
        if (drawRef.current) {
          map.removeControl(drawRef.current);
        }
        if (selectedOption === null) return;

        if (
          (isMobile && selectedOption === "Photos Plan") ||
          selectedOption ===
            planConfig?.ground_control?.ground_control_points_plan
        ) {
          map.doubleClickZoom.disable(); // Disable double-click zoom
        }

        if (
          isMobile &&
          selectedOption ===
            planConfig?.ground_control?.ground_control_points_plan
        ) {
          map.doubleClickZoom.disable(); // Disable double-click zoom
        }

        // custom draw styles: increase line/polygon stroke width so circles/polygons are easier to see
        const drawStyles = [
          {
            id: "gl-draw-line",
            type: "line",
            filter: ["==", "$type", "LineString"],
            paint: {
              "line-color": "rgb(255, 134, 52)",
              "line-width": 6, // increased from default for better visibility
            },
          },
          {
            id: "gl-draw-polygon-stroke",
            type: "line",
            filter: ["==", "$type", "Polygon"],
            paint: {
              "line-color": "rgb(255, 134, 52)",
              "line-width": 6, // increased stroke for polygon/circle edges
            },
          },
          {
            id: "gl-draw-polygon-fill",
            type: "fill",
            filter: ["==", "$type", "Polygon"],
            paint: {
              "fill-color": "rgba(255, 134, 52, 0.08)",
            },
          },
          // keep default point styling small but visible
          {
            id: "gl-draw-point",
            type: "circle",
            filter: ["==", "$type", "Point"],
            paint: {
              "circle-radius": 5,
              "circle-color": "#fff",
              "circle-stroke-width": 2,
              "circle-stroke-color": "rgb(255, 134, 52)",
            },
          },
        ];

        const draw = new MapboxDraw({
          displayControlsDefault: false,
          modes: {
            ...MapboxDraw.modes,
            draw_circle: DrawCircle,
          },
          controls: {
            point:
              selectedOption === "Photos Plan" ||
              selectedOption ===
                planConfig?.ground_control?.ground_control_points_plan ||
              !isPhotoDraw,
            line_string: selectedOption === "Videos Plan",
            polygon:
              selectedOption === "Photogrammetry Plan" ||
              selectedOption === "Lidar Plan" ||
              isPhotoDraw,
            trash: true,
          },
          styles: drawStyles,
        });

        map.addControl(draw);
        drawRef.current = draw;
        safeResize();

        if (selectedOption === "Videos Plan") {
          draw.changeMode("draw_line_string");
          safeResize();
        }
        if (
          selectedOption === "Photogrammetry Plan" ||
          selectedOption === "Lidar Plan" ||
          isPhotoDraw
        ) {
          draw.changeMode("draw_polygon");
          safeResize();
        }

        const updateDraw = (e) => {
          let data = { features: [] };
          try {
            data = draw.getAll(); // Get all existing drawn features
          } catch (error) {}

          if (selectedOption === "Videos Plan") {
            const newFeatures = e.features; // Newly drawn features
            const allFeatures = data.features; // Get all existing features from draw.getAll()

            setLineCoords((prevCoords) => {
              const newCoords = newFeatures.map((f) => f.geometry.coordinates);
              return [...prevCoords, ...newCoords];
            });

            setVideoPlanData((prev) => {
              const updatedData = [...allFeatures]; // Use all existing features to prevent data loss

              newFeatures.forEach((newFeature) => {
                const existingIndex = updatedData.findIndex(
                  (item) => item.id === newFeature.id
                );
                if (existingIndex !== -1) {
                  updatedData[existingIndex] = newFeature; // Update existing feature
                } else {
                  updatedData.push(newFeature); // Add new feature
                }
              });

              return updatedData;
            });

            setVideoPlanLine((prev) => {
              const updatedLines = [
                ...allFeatures.filter((f) => f.geometry.type === "LineString"),
              ]; // Keep existing lines

              newFeatures.forEach((newFeature) => {
                if (newFeature.geometry.type === "LineString") {
                  const existingIndex = updatedLines.findIndex(
                    (item) => item.id === newFeature.id
                  );
                  if (existingIndex !== -1) {
                    updatedLines[existingIndex] = newFeature; // Update existing feature
                  } else {
                    updatedLines.push(newFeature); // Add new feature
                  }
                }
              });

              return updatedLines;
            });

            setLineStrings((prev) => {
              const newFeatureIds = new Set(data.features.map((f) => f.id));
              return [
                ...prev.filter((item) => !newFeatureIds.has(item.id)), // Keep unchanged features
                ...data.features, // Add or replace updated features
              ];
            });
          }

          if (
            selectedOption === "Photogrammetry Plan" ||
            selectedOption === "Lidar Plan"
          ) {
            if (data.features.length > 0) {
              const area = turf.area(data);
              setRoundedArea(area);
              const coords = data.features[0].geometry.coordinates[0];

              setLineStrings((prev) => {
                const newFeatureIds = new Set(data.features.map((f) => f.id));
                return [
                  ...prev.filter((item) => !newFeatureIds.has(item.id)), // Keep unchanged features
                  ...data.features, // Add or replace updated features
                ];
              });

              setLineCoords((prevCoords) => [...prevCoords, ...coords]);
            } else {
              setRoundedArea();
            }
          }
        };
        // Listen for when a LineString is drawn
        // mapRef.current.on("draw.create", updateDraw);
        mapRef.current.on("draw.delete", (e) => {
          const deletedFeatureIds = e.features.map((feature) => feature.id);

          // Update state for Videos Plan
          if (selectedOption === "Videos Plan") {
            setVideoPlanData((prev) =>
              prev.filter((feature) => !deletedFeatureIds.includes(feature.id))
            );

            setVideoPlanLine((prev) =>
              prev.filter((feature) => !deletedFeatureIds.includes(feature.id))
            );

            setLineCoords((prevCoords) =>
              prevCoords.filter((coords) =>
                e.features.every(
                  (feature) =>
                    !feature.geometry.coordinates.some(
                      (coord) =>
                        JSON.stringify(coord) === JSON.stringify(coords)
                    )
                )
              )
            );
          }

          // Update state for Photogrammetry Plan and Lidar Plan
          if (
            selectedOption === "Photogrammetry Plan" ||
            selectedOption === "Lidar Plan"
          ) {
            setLineStrings((prev) =>
              prev.filter((feature) => !deletedFeatureIds.includes(feature.id))
            );

            setLineCoords((prevCoords) =>
              prevCoords.filter((coords) =>
                e.features.every(
                  (feature) =>
                    !feature.geometry.coordinates[0].some(
                      (coord) =>
                        JSON.stringify(coord) === JSON.stringify(coords)
                    )
                )
              )
            );
          }

          // Synchronize projectData with the updated state
          setProjectData((prevData) =>
            prevData.map((data) => {
              if (data.selectedOption === selectedOption) {
                return {
                  ...data,
                  features: data.features.filter(
                    (feature) => !deletedFeatureIds.includes(feature.id)
                  ),
                };
              }
              return data;
            })
          );
        });
        mapRef.current.on("draw.update", (e) => {
          updateDraw(e);
        });
        // Add click event listener for desktop
        map.on("click", (event) => {
          if (!isMobile) {
            handleMapInteraction(event);
          }
        });

        // Add touchstart event listener for mobile
        let lastTapTime = 0;

        // Detect double-tap
        map.on("touchstart", (event) => {
          const currentTime = new Date().getTime();
          const tapGap = currentTime - lastTapTime;

          if (tapGap < 300 && tapGap > 0) {
            if (isMobile) {
              // Double-tap detected!
              handleMapInteraction(event);
            }
          }

          lastTapTime = currentTime;
        });

        function handleMapInteraction(event) {
          if (selectedOption === "Videos Plan") {
            setInstruction(
              "Click a second time to set a waypoint for a turn OR click twice to mark where the video flight ends"
            );
          }
          if (selectedOption === "Photos Plan") {
            handleMapClick(event);
            setInstruction(
              "Click on the blue camera and rotate in order to set the heading of the drone picture"
            );
          }

          if (selectedOption === "Photogrammetry Plan") {
            setInstruction(
              "Click once to set your next boundary marker OR double click to finish your measurement."
            );
          }
          if (selectedOption === "Lidar Plan") {
            setInstruction(
              "Click once to set your next boundary marker OR double click to finish your measurement."
            );
          }

          if (
            selectedOption ===
            planConfig?.ground_control?.ground_control_points_plan
          ) {
            handleMapClick(event);
          }
        }

        // Detect when drawing is completed
        map.on("draw.create", function (e) {
          updateDraw(e);
          setTimeout(() => {
            if (selectedOption === "Videos Plan") {
              // videoSelectRef?.current?.click();
              setInstruction(
                "Click line or circle to add another video location"
              );
            } else if (selectedOption === "Photogrammetry Plan") {
              // grammeSelectRef?.current?.click();
            } else if (selectedOption === "Lidar Plan") {
              // lidarSelectRef?.current?.click();
            }
            safeResize();
          }, 0);
        });
      }
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  }, [selectedOption, mapRef.current, isPhotoDraw]);

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { longitude, latitude } = position.coords;
          const locationData = {
            longitude: longitude,
            latitude: latitude,
            zoom: 14,
          };
          localStorage.setItem(
            "current_location",
            JSON.stringify(locationData)
          );
          setViewState(locationData);
          mapRef?.current?.getMap()?.flyTo({
            center: [longitude, latitude],
            zoom: 13,
          });
        },
        (error) => {
          console.log("Error getting location:", error.message);
        }
      );
    } else {
      console.log("Geolocation is not available in this browser.");
    }
  }, [mapRef]);

  const handleMapClick = (e) => {
    const isDraw = localStorage.getItem("photo_draw");
    if (isDraw) {
      return;
    }
    e.preventDefault();
    if (selectedOption === "Photos Plan") {
      const newMarker = {
        properties: {
          id: crypto.randomUUID(),
          direction: 0,
        },
        geometry: {
          type: "Point",
          coordinates: [e.lngLat.lng, e.lngLat.lat],
        },
      };
      setMarkers((prevMarkers) => [...prevMarkers, newMarker]);
    }
    if (
      selectedOption === planConfig?.ground_control?.ground_control_points_plan
    ) {
      const newMarker = {
        properties: {
          id: crypto.randomUUID(),
          direction: 0,
        },
        geometry: {
          type: "Point",
          coordinates: [e.lngLat.lng, e.lngLat.lat],
        },
      };
      setGroundControlMarkers((prevMarkers) => [...prevMarkers, newMarker]);
    }
  };

  useEffect(() => {
    const edit = localStorage.getItem("edit_project");
    let isNew = localStorage.getItem("project_add_more");

    if (isNew) {
      setCurrentStep(2);
      let coords = projectData?.[0]?.result?.coordinates || [];

      if (coords?.length) {
        // Recursively get the first numeric pair
        const getFirstCoord = (arr) => {
          if (!Array.isArray(arr)) return null;
          if (typeof arr[0] === "number" && typeof arr[1] === "number")
            return arr;
          if (Array.isArray(arr[0])) return getFirstCoord(arr[0]);
          return null;
        };

        const center = getFirstCoord(coords) || coords[0];

        mapRef?.current?.getMap()?.flyTo({
          center: center,
          zoom: 18,
        });
      }
    } else if (edit) {
      setIsEdit(true);
      setCurrentStep(4);
    } else {
      setIsEdit(false);
      setCurrentStep(0);
    }
    const fetchPrices = async () => {
      try {
        const response = await priceList();
        setPriceData(response);
      } catch (error) {
        console.log("Error fetching prices:", error);
      }
    };
    fetchPrices();
  }, [mapRef?.current]);

  // In edit flow, if a plan is selected externally (e.g., from Preview -> Add More)
  // preselect and load the most recent sub-service for that plan so edits update instead of duplicating
  useEffect(() => {
    if (!isEdit) return;
    if (currentStep !== 4) return;
    if (!selectedOption) return;
    if (selectedSubServiceKey) return; // already set via sub-service click

    const existingForPlan = [...projectData]
      .filter((d) => d.selectedOption === selectedOption)
      .sort((a, b) => (a.zoomLevel || 0) - (b.zoomLevel || 0));
    let editId = localStorage.getItem("edit_project_id");
    let idx = existingForPlan.length - 1;
    const getEditIdIndx = existingForPlan.findIndex((i) => i.id === editId);
    if (getEditIdIndx !== -1) {
      idx = getEditIdIndx;
    }
    const last = existingForPlan[idx];
    if (last && last.subServiceKey) {
      handleOptionChange({
        plan: selectedOption,
        mainServiceKey: last.mainServiceKey || null,
        mainServiceLabel: last.mainServiceLabel || null,
        subServiceKey: last.subServiceKey,
        subServiceLabel: last.subServiceLabel || null,
      });
    }
  }, [isEdit, currentStep, selectedOption, projectData]);

  useEffect(() => {
    if (!selectedOption) return;
    if (selectedOption === "Photos Plan") {
      let message = "Click where you want drone picture";
      if (isMobile) {
        message = "Dabble click where you want drone picture";
      }
      setInstruction(message);
    } else if (selectedOption === "Videos Plan") {
      setInstruction("Click where you want the video flight to begin.");
    } else if (
      selectedOption === planConfig?.ground_control?.ground_control_points_plan
    ) {
      let message = "Click where you want picture";
      setInstruction(message);
    } else {
      setInstruction("Click once to start your area.");
    }
  }, [selectedOption]);

  useEffect(() => {
    if (isEdit) {
      // const response = localStorage.getItem("projectData") || null;
      let resultArr = projectData;

      const results = filterProjectData(resultArr);
      let editId = localStorage.getItem("edit_project_id");
      let isNew = localStorage.getItem("project_add_more");

      if (isNew && currentStep === 2) {
        setSelectedMainServiceKey(null);
        handleOptionChange({
          plan: null,
          mainServiceKey: null,
          mainServiceLabel: null,
          subServiceKey: null,
          subServiceLabel: null,
        });
      }

      const data = results.find((data) => {
        return (
          data.selectedOption === selectedOption &&
          (!editId || data.id === editId)
        );
      });

      if (!data) return;

      const draw = drawRef?.current;
      // Fly to the saved location
      const coords = getCoordinatesFromData(data, selectedOption);
      if (coords?.length) {
        // Recursively get the first numeric pair
        const getFirstCoord = (arr) => {
          if (!Array.isArray(arr)) return null;
          if (typeof arr[0] === "number" && typeof arr[1] === "number")
            return arr;
          if (Array.isArray(arr[0])) return getFirstCoord(arr[0]);
          return null;
        };

        const center = getFirstCoord(coords) || coords[0];

        console.log(">>>center>>", center);
        mapRef?.current?.getMap()?.flyTo({
          center: center,
          zoom: data.zoomLevel || 18,
        });
      }

      // Set the draw instance with the existing data

      if (selectedOption === "Photos Plan") {
        draw?.deleteAll();
        setMarkers(data.features);
      }

      if (
        selectedOption ===
        planConfig?.ground_control?.ground_control_points_plan
      ) {
        draw?.deleteAll();
        setGroundControlMarkers(data.features);
      }

      // Update state with the existing data
      if (selectedOption === "Videos Plan") {
        setEditInstruction({
          message:
            "Click here, then click on your desired area on the map to select it.",
          position: {
            top: 0,
            right: 40,
          },
          isShow: true,
        });
        draw?.deleteAll();
        draw?.set({
          type: "FeatureCollection",
          features: data.features,
        });
        // Get the feature ID (assuming one feature; loop if multiple)
        const featureId = data.features[0]?.id || draw.getAll().features[0]?.id;

        if (featureId) {
          // Select and make the feature editable
          draw?.changeMode("direct_select", { featureId });
        }

        // draw?.changeMode("simple_select", {
        //   featureIds: data.features.map((f) => f.id),
        // });
        // draw?.changeMode("draw_line_string");

        setVideoPlanData(data.features);
        setVideoPlanLine(
          data.features.filter(
            (feature) => feature.geometry.type === "LineString"
          )
        );
        setLineCoords(
          data.features.map((feature) => feature.geometry.coordinates)
        );
      } else if (
        selectedOption === "Photogrammetry Plan" ||
        selectedOption === "Lidar Plan"
      ) {
        draw?.deleteAll();
        draw?.set({
          type: "FeatureCollection",
          features: data.features,
        });
        // Get the feature ID (assuming one feature; loop if multiple)
        const featureId = data.features[0]?.id || draw.getAll().features[0]?.id;

        if (featureId) {
          // Select and make the feature editable
          draw?.changeMode("direct_select", { featureId });
        }

        // draw?.changeMode("simple_select", {
        //   featureIds: data.features.map((f) => f.id),
        // });
        // draw?.changeMode("draw_polygon");
        setEditInstruction({
          message:
            "Click here, then click on your desired area on the map to select it.",
          position: {
            top: 0,
            right: 40,
          },
          isShow: true,
        });

        setLineStrings(data.features);
        setLineCoords(
          data.features.map((feature) => feature.geometry.coordinates[0])
        );
      }
    }
  }, [
    isEdit,
    selectedOption,
    drawRef?.current,
    mapRef.current,
    currentStep,
    isPhotoDraw,
  ]);

  const getCoordinatesFromData = (data, option) => {
    if (!data?.features?.[0]) return [];

    switch (option) {
      case "Photos Plan":
        return data.features[0]?.geometry?.coordinates || [];
      case "Videos Plan":
        return data.features[0]?.geometry?.coordinates?.[0] || [];
      case "Photogrammetry Plan":
      case "Lidar Plan":
        const arr = data.features[0]?.geometry?.coordinates?.[0];
        return Array.isArray(arr) ? arr[0] : [];
      case planConfig?.ground_control?.ground_control_points_plan:
        return data.features[0]?.geometry?.coordinates || [];
      default:
        return [];
    }
  };

  const filterProjectData = (projects) => {
    const uniqueArray = projects.reduce((acc, data) => {
      const key = `${data.selectedOption}::${data.subServiceKey || "default"}`;
      const existingIndex = acc.findIndex(
        (obj) =>
          `${obj.selectedOption}::${obj.subServiceKey || "default"}` === key
      );

      if (existingIndex !== -1) {
        if (data.features.length > 0) {
          acc[existingIndex] = data;
        }
      } else {
        acc.push(data);
      }

      return acc;
    }, []);

    // Process data based on selected option
    const resultData = uniqueArray.map((data) => {
      if (data.selectedOption === "Videos Plan") {
        const polygonData = videoPlanData.filter(
          (d) => d.properties.isCircle === true
        );
        let features = [...videoPlanLine, ...polygonData];
        if (features.length === 0) {
          features = data.features;
        }
        return {
          ...data,
          features: features, // Ensure updated features
        };
      }
      return data;
    });

    return resultData;
  };

  const isOptionAdded = (option) => {
    return projectData?.some((data) => data?.selectedOption === option);
  };

  const handleOptionChange = (option) => {
    console.log("OnChange");
    // Change the selected option
    // Show video instructions
    const isObject = typeof option === "object" && option !== null;
    const plan = isObject ? option.plan : option;
    setInstructionType(plan);
    setSelectedOption(plan);
    // capture sub-service metadata if provided
    if (isObject) {
      setSelectedMainServiceKey(option.mainServiceKey || null);
      setSelectedMainServiceLabel(option.mainServiceLabel || null);
      setSelectedSubServiceKey(option.subServiceKey || null);
      setSelectedSubServiceLabel(option.subServiceLabel || null);
    } else {
      setSelectedMainServiceKey(null);
      setSelectedMainServiceLabel(null);
      setSelectedSubServiceKey(null);
      setSelectedSubServiceLabel(null);
    }
    setOpenMenu(false);
    // setIsEdit(true);

    // If we're in step-based flow, stay in step 3 for sub-service selection
    if (currentStep === 3) {
      // Don't change step, just update the selected option
    } else {
      // For legacy mode, save current data before changing the option
      const storeData = handleProjectSave();
      if (storeData) {
        setProjectData((prevData) => {
          const updatedData = prevData.map((item) =>
            item.id === storeData.id ? storeData : item
          );

          const isDuplicate = prevData.some((data) => data.id === storeData.id);
          return isDuplicate ? updatedData : [...prevData, storeData];
        });
      }
    }

    // Reset state variables based on the selected option
    const clearStatesForPlan = (p) => {
      console.log(
        ">ss>>>>",
        p === planConfig?.ground_control?.ground_control_points_plan
      );
      if (p === "Photos Plan") {
        setMarkers([]);
        return;
      }
      if (p === "Videos Plan") {
        setLineCoords([]);
        setVideoPlanData([]);
        setVideoPlanLine([]);
        setLineStrings([]);
        try {
          drawRef?.current?.deleteAll();
        } catch (e) {}
        return;
      }
      if (p === "Photogrammetry Plan" || p === "Lidar Plan") {
        setLineCoords([]);
        setLineStrings([]);
        setRoundedArea(null);
        try {
          drawRef?.current?.deleteAll();
        } catch (e) {}
        return;
      }
      if (p === planConfig?.ground_control?.ground_control_points_plan) {
        setGroundControlMarkers([]);
        return;
      }
    };

    clearStatesForPlan(plan);

    // Load saved geometry for this plan+subservice if it exists
    const existing = projectData.find(
      (d) =>
        d.selectedOption === plan &&
        (d.subServiceKey || "default") ===
          (isObject ? option.subServiceKey || "default" : "default")
    );
    const getMappingArea = projectData.find(
      (d) =>
        d.selectedOption === "Photogrammetry Plan" &&
        (d.subServiceKey || "default") ===
          (isObject ? "mapping-area" || "default" : "default")
    );

    if (existing) {
      try {
        if (plan === "Photos Plan") {
          setMarkers(existing.features || []);
        } else if (
          plan === planConfig?.ground_control?.ground_control_points_plan
        ) {
          setGroundControlMarkers(existing.features || []);
          console.log(">>>getMappingArea>>", getMappingArea);
          if (
            getMappingArea &&
            option.subServiceKey === "ground-control-orthomosaic"
          ) {
            drawRef?.current?.deleteAll();
            drawRef?.current?.set({
              type: "FeatureCollection",
              features: getMappingArea.features,
            });
          }
        } else if (plan === "Videos Plan") {
          const features = existing.features || [];
          drawRef?.current?.deleteAll();
          drawRef?.current?.set({ type: "FeatureCollection", features });
          setVideoPlanData(features);
          setVideoPlanLine(
            features.filter((f) => f.geometry?.type === "LineString")
          );
          setLineCoords(
            features.map((f) => f.geometry?.coordinates).filter(Boolean)
          );
        } else if (plan === "Photogrammetry Plan" || plan === "Lidar Plan") {
          const features = existing.features || [];
          drawRef?.current?.deleteAll();
          drawRef?.current?.set({ type: "FeatureCollection", features });
          setLineStrings(features);
          if (features.length) {
            try {
              setRoundedArea(
                turf.area({ type: "FeatureCollection", features })
              );
            } catch (e) {}
          }
        }
        // fly to saved coords
        const coords = getCoordinatesFromData(existing, plan);
        console.log(">getCoordinatesFromData>>>>", coords);
        if (coords?.length) {
          let center = coords;
          if (Array.isArray(coords[0])) center = coords[0];
          mapRef?.current
            ?.getMap()
            ?.flyTo({ center, zoom: existing.zoomLevel || 17 });
        }
      } catch (e) {
        console.error("Error loading saved subservice data", e);
      }
    } else {
      if (plan === planConfig?.ground_control?.ground_control_points_plan) {
        setGroundControlMarkers([]);
        if (
          getMappingArea &&
          option.subServiceKey === "ground-control-orthomosaic"
        ) {
          drawRef?.current?.deleteAll();
          drawRef?.current?.set({
            type: "FeatureCollection",
            features: getMappingArea.features,
          });
        }
      }
    }
  };

  const handleSearchChange = async (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query) {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            query
          )}.json?access_token=${MAPBOX_TOKEN}`
        );
        const data = await response.json();
        setAddressSuggestions(data.features);
      } catch (error) {
        console.error("Error fetching location:", error);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleSearchSubmit = async () => {
    if (!searchQuery) return;

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          searchQuery
        )}.json?access_token=${MAPBOX_TOKEN}`
      );
      const data = await response.json();
      const firstResult = data.features[0];
      setAddressSuggestions([]);

      if (firstResult) {
        const [longitude, latitude] = firstResult.center;

        mapRef.current.getMap().flyTo({
          center: [longitude, latitude],
          zoom: 16,
        });

        setSearchLocation({ latitude, longitude });
      }
    } catch (error) {
      console.error("Error fetching location:", error);
    }
  };

  const handleAddressSelect = (address) => {
    const [longitude, latitude] = address.center;

    mapRef.current.getMap().flyTo({
      center: [longitude, latitude],
      zoom: 16,
    });

    setSearchLocation({ latitude, longitude });
    setSearchQuery(address.place_name);
    setAddressSuggestions([]);
  };

  function calculateImagePrice() {
    if (!markers.length) {
      return null;
    }
    const priceInfo = priceData.find((item) => item.id === 1);

    const basePrice = priceInfo?.basePrice || 379; // base price
    const maxImages = priceInfo?.max || 20; //Up to 20 images
    const extraImagePrice = priceInfo?.extraPrice || 5; // extra image price
    const totalImages = markers.length; //total images selected
    let price = 0;
    const cords = markers.map((marker, index) => {
      //if under the base price total price will be base price
      if (totalImages <= maxImages) {
        price = basePrice;
      } else {
        //calculate extra image price
        const extraImages = totalImages - maxImages; //extra images count
        const extraPay = extraImages * extraImagePrice; //e.g. 2*5 = $10
        price = basePrice + extraPay; //extra pay   //e.g. $10 + $379  =  $389
      }
      return marker.geometry.coordinates;
    });
    const data = {
      price,
      totalImages: totalImages,
      coordinates: cords,
      type: selectedOption,
    };
    return data;
  }

  function calculateGroundControlPrice() {
    if (!groundControlMarkers.length) {
      return null;
    }
    const priceInfo = priceData.find(
      (item) => item.id === planConfig?.ground_control?.id
    );

    const basePrice =
      priceInfo?.basePrice || planConfig?.ground_control?.basePrice; // base price
    const totalImages = groundControlMarkers.length; //total images selected
    const maxImages = priceInfo?.max || planConfig?.ground_control?.max; //Up to 20 images
    const extraImagePrice =
      priceInfo?.extraPrice || planConfig?.ground_control?.extraPrice; // extra image price
    let price = 0;
    const cords = groundControlMarkers.map((marker, index) => {
      //if under the base price total price will be base price
      if (totalImages <= maxImages) {
        price = basePrice;
      } else {
        //calculate extra image price
        const extraImages = totalImages - maxImages; //extra images count
        const extraPay = extraImages * extraImagePrice; //e.g. 2*5 = $10
        price = basePrice + extraPay; //extra pay   //e.g. $10 + $379  =  $389
      }
      return marker.geometry.coordinates;
    });
    const data = {
      price: Math.floor(price * totalImages),
      each_price: price,
      totalImages: totalImages,
      coordinates: cords,
      type: selectedOption,
    };
    return data;
  }
  const calculateVideoPrice = () => {
    const planData = [...videoPlanData, ...videoPlanLine];
    if (planData.length === 0) return;

    let totalMiles = 0;
    const polygonData = planData.find(
      (item) => item?.geometry?.type === "Polygon"
    );
    if (polygonData) {
      totalMiles = polygonData.properties.radiusInKm * 0.621371;
    } else {
      totalMiles = turf.length(planData[0], { units: "miles" }).toFixed(2);
    }
    const priceInfo = priceData?.find((item) => item.id === 2);
    const basePrice = priceInfo?.basePrice || 250; // Base price for up to 0.5 miles
    const maxMiles = priceInfo?.max || 0.5; // Maximum miles included in base price
    const extraPricePerMile = priceInfo?.extraPrice || 65; // Additional price per extra mile

    let price = basePrice;

    /* 
    
    Example 1: Total Miles = 1.97
    Extra miles: 1.97 - 0.5 = 1.47
    Rounded up: Math.ceil(1.47) = 2
    Extra charge: 2 * 65 = $130
    Total Price: $250 + $130 = $380

    */

    if (totalMiles > maxMiles) {
      const extraMiles = totalMiles - maxMiles; // Extra miles beyond 0.5
      const fullExtraMiles = Math.ceil(extraMiles); // Round up to next full mile eg.
      price += fullExtraMiles * extraPricePerMile;
    }

    // Round price to 2 decimal places for accuracy
    price = parseFloat(price?.toFixed(2));

    const data = {
      price,
      area: totalMiles,
      parameter: "miles",
      coordinates: lineCoords,
      type: selectedOption,
    };

    return data;
  };

  const calculatePhotogrammetryPrice = (type = "photo") => {
    if (!roundedArea) {
      return null;
    }
    // Convert square meters to acres
    const totalAcres = (roundedArea / 4046.86).toFixed(2);
    const planId = type === "photo" ? 3 : 4;
    const priceInfo = priceData?.find((item) => item.id === planId);
    const basePrice = priceInfo?.basePrice || 649; // Base price for up to 10 acres
    const maxAcres = priceInfo?.max || 10; // Maximum acres covered in base price
    const extraPricePerAcre = priceInfo?.extraPrice || 7; // Extra charge per additional acre
    let price = basePrice;

    /* 
    
    Example 1: If area is 8 acres
    Total Acres: 8
    Base Price: $649 (since it's ≤ 10 acres)
    Extra Charge: $0
    Total Price: $649


    Example 2: If area is 15 acres
    Total Acres: 15
    Extra Acres: 15 - 10 = 5
    Extra Charge: 5 * $7 = $35
    Total Price: $649 + $35 = $684

    */

    // Calculate extra cost if acres exceed 10
    if (totalAcres > maxAcres) {
      const extraAcres = Math.ceil(totalAcres - maxAcres); // Round up
      price += extraAcres * extraPricePerAcre;
    }

    // Round total price to two decimal places
    price = parseFloat(price.toFixed(2));

    const data = {
      price,
      area: parseFloat(totalAcres),
      parameter: type === "acres",
      coordinates: lineCoords,
      type: selectedOption,
    };

    return data;
  };

  const handleProjectSave = () => {
    if (selectedOption === null) {
      return;
    }

    function calculatePrice() {
      if (selectedOption === "Photos Plan") return calculateImagePrice();
      if (selectedOption === "Videos Plan") return calculateVideoPrice();
      if (selectedOption === "Photogrammetry Plan")
        return calculatePhotogrammetryPrice();
      if (selectedOption === "Lidar Plan")
        return calculatePhotogrammetryPrice("lidar");
      if (
        selectedOption ===
        planConfig?.ground_control?.ground_control_points_plan
      )
        return calculateGroundControlPrice();
    }

    const result = calculatePrice();

    if (!result) return;

    let features = lineStrings;
    if (selectedOption === "Photos Plan") {
      features = markers;
    }
    if (
      selectedOption === planConfig?.ground_control?.ground_control_points_plan
    ) {
      features = groundControlMarkers;
    }
    const storeData = {
      result,
      selectedOption,
      features: features,
      id: `${Date.now()}-${selectedOption}-${
        selectedSubServiceKey || "default"
      }`,
      zoomLevel: parseInt(mapRef.current.getZoom()),
      // sub-service metadata
      mainServiceKey: selectedMainServiceKey,
      mainServiceLabel: selectedMainServiceLabel,
      subServiceKey: selectedSubServiceKey,
      subServiceLabel: selectedSubServiceLabel,
      from_new_ui: true,
    };

    return storeData;
  };

  const handleSave = (isEdit = false) => {
    const storeData = handleProjectSave();
    if (!storeData) {
      if (projectData.length === 0) {
        toast.info("Please select at least one plan to continue", {
          toastId: Date.now(),
          autoClose: 1500,
        });
        return;
      }
    } else {
      setProjectData((prevData) => {
        const key = `${storeData.selectedOption}::${
          storeData.subServiceKey || "default"
        }`;
        const existingIndex = prevData.findIndex(
          (d) => `${d.selectedOption}::${d.subServiceKey || "default"}` === key
        );
        if (existingIndex !== -1) {
          const copy = [...prevData];
          copy[existingIndex] = storeData;
          return copy;
        }
        return [...prevData, storeData];
      });
    }

    // Reset step-based flow
    if (currentStep > 0) {
      setCurrentStep(0);
      setSelectedOption("");
    } else {
      setSelectedOption("");
    }

    if (!isEdit) {
      setShowAlert(true);
    }
  };

  // Inline save: used by sub-service cards. Does not open popup or change steps.
  const handleInlineSave = () => {
    const storeData = handleProjectSave();

    if (!storeData) {
      if (projectData.length === 0) {
        return projectData;
      }
      return projectData;
    }

    let newData;
    setProjectData((prevData) => {
      const key = `${storeData.selectedOption}::${
        storeData.subServiceKey || "default"
      }`;
      const existingIndex = prevData.findIndex(
        (d) => `${d.selectedOption}::${d.subServiceKey || "default"}` === key
      );

      if (existingIndex !== -1) {
        const copy = [...prevData];
        copy[existingIndex] = storeData;
        newData = copy;
        return copy;
      }

      newData = [...prevData, storeData];
      return newData;
    });

    // return the snapshot so Sidebar can use it
    return newData || projectData;
  };

  const handleContinueAll = async () => {
    await handleSubmitData(isEdit, projectData);
  };

  const handleSubmitData = async (
    isEdit = false,
    updatedProjectData = projectData
  ) => {
    // Ensure we store only the latest updates for each `selectedOption`
    const uniqueArray = updatedProjectData.reduce((acc, data) => {
      const key = `${data.selectedOption}::${data.subServiceKey || "default"}`;
      const existingIndex = acc.findIndex(
        (obj) =>
          `${obj.selectedOption}::${obj.subServiceKey || "default"}` === key
      );

      if (existingIndex !== -1) {
        if (data.features.length > 0) {
          acc[existingIndex] = data;
        }
      } else {
        acc.push(data);
      }

      return acc;
    }, []);

    // Process data based on selected option
    let resultData = uniqueArray.map((data) => {
      if (data.selectedOption === "Videos Plan") {
        const polygonData = videoPlanData.filter(
          (d) => d.properties.isCircle === true
        );
        let features = [...videoPlanLine, ...polygonData];
        if (features.length === 0) {
          features = data.features;
        }
        return {
          ...data,
          features: features, // Ensure updated features
        };
      }
      return data;
    });

    let cords = [];
    if (resultData[0]?.selectedOption === "Photos Plan") {
      cords = resultData?.[0]?.features?.[0]?.geometry?.coordinates;
    } else if (resultData[0]?.selectedOption === "Videos Plan") {
      cords = resultData?.[0]?.features?.[0]?.geometry?.coordinates?.[0];
    } else if (
      resultData[0]?.selectedOption ===
      planConfig?.ground_control?.ground_control_points_plan
    ) {
      cords = resultData?.[0]?.features?.[0]?.geometry?.coordinates;
    } else {
      cords = resultData?.[0]?.features?.[0]?.geometry?.coordinates?.[0][0];
    }

    const address = await getAddressFromCoords(cords);
    // Filter out plans with empty features
    resultData = resultData.filter((data) => data.features.length > 0);

    // Check if all plans have empty features
    if (resultData.length === 0) {
      toast.error(
        "All plans have empty features. Please add data to proceed.",
        {
          toastId: "empty-features-toast",
          autoClose: 2000,
        }
      );
      throw new Error(
        "All plans have empty features. Please add data to proceed."
      );
    }
    const projectName =
      prName || searchQuery || address || `Project ${moment().format("lll")}`;
    const id = localStorage.getItem("edit_id");

    if (isEdit && id) {
      const price = resultData.reduce(
        (acc, curr) => acc + curr.result.price,
        0
      );
      const editData = {
        // name: projectName || "untitled",
        plans: JSON.stringify(resultData),
        price,
        address: searchQuery || address,
      };

      await editProject(id, editData);
      const redirectPage = searchParams.get("redirect");
      localStorage.removeItem("edit_project");
      localStorage.removeItem("edit_id");
      localStorage.removeItem("project_add_more");
      localStorage.removeItem("edit_project_id");
      localStorage.removeItem("projectData");
      setProjectData([]);
      setSelectedOption("");
      setMarkers([]);
      setLineCoords([]);
      setLineStrings([]);
      setVideoPlanData([]);
      setVideoPlanLine([]);
      setRoundedArea(null);
      setIsEdit(false);
      navigate(redirectPage);
    } else {
      localStorage.setItem(
        "projectData",
        JSON.stringify({
          data: resultData,
          projectName: projectName || "untitled",
          address,
        })
      );
      localStorage.removeItem("edit_project");
      localStorage.removeItem("edit_project_id");
      localStorage.removeItem("edit_id");
      localStorage.removeItem("project_add_more");
      setProjectData(resultData);
      navigate("/dashboard/preview");
    }
  };

  const handleEdit = async () => {
    // Show loading toast
    const loadingToastId = toast.loading("Saving your changes...");

    try {
      // Save the current project data and get the updated data
      const updatedProjectData = await new Promise((resolve) => {
        const storeData = handleProjectSave();
        if (storeData) {
          setProjectData((prevData) => {
            const updatedData = prevData.map((item) =>
              item.id === storeData.id ? storeData : item
            );

            const isDuplicate = prevData.some(
              (data) => data.id === storeData.id
            );
            const finalData = isDuplicate
              ? updatedData
              : [...prevData, storeData];
            resolve(finalData); // Resolve with the updated data
            return finalData;
          });
        } else {
          resolve(projectData); // Resolve with the current data if no changes
        }
      });

      // Submit the updated project data
      await handleSubmitData(true, updatedProjectData);

      // Update the loading toast to success
      toast.update(loadingToastId, {
        render: "Changes saved successfully!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
    } catch (error) {
      // Update the loading toast to error
      toast.update(loadingToastId, {
        render: "Failed to save changes. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
      console.error("Error while editing:", error);
    }
  };

  const allPlansAdded = () => {
    const requiredPlans = [
      "Photos Plan",
      "Videos Plan",
      "Photogrammetry Plan",
      "Lidar Plan",
      planConfig?.ground_control?.ground_control_points_plan,
    ];
    return requiredPlans.every((plan) =>
      projectData.some((data) => data.selectedOption === plan)
    );
  };

  const isSelectedOption = (option) => {
    return !!projectData.find((data) => data.selectedOption === option);
  };

  const isSelectedSubService = (plan, subKey) => {
    return !!projectData.find(
      (data) =>
        data.selectedOption === plan &&
        (data.subServiceKey || "default") === (subKey || "default")
    );
  };

  const getCurrentFeaturesForPlan = (plan) => {
    if (plan === "Photos Plan") return markers;
    if (plan === "Videos Plan") return [...videoPlanLine, ...videoPlanData];
    if (plan === planConfig?.ground_control?.ground_control_points_plan)
      return groundControlMarkers;
    if (plan === "Photogrammetry Plan" || plan === "Lidar Plan")
      return lineStrings;
    return [];
  };

  const isActiveSubserviceDirty = (plan, subKey) => {
    try {
      const current = getCurrentFeaturesForPlan(plan) || [];
      const saved = projectData.find(
        (d) =>
          d.selectedOption === plan &&
          (d.subServiceKey || "default") === (subKey || "default")
      );
      const currStr = JSON.stringify(current);
      const savedStr = JSON.stringify(saved?.features || []);
      return currStr !== savedStr && current.length > 0;
    } catch (e) {
      return false;
    }
  };

  const planHasGeometry = (plan) => {
    if (plan === "Photos Plan") return markers.length > 0;
    if (plan === "Videos Plan")
      return videoPlanLine.length > 0 || videoPlanData.length > 0;
    if (plan === planConfig?.ground_control?.ground_control_points_plan)
      return groundControlMarkers.length > 0;
    if (plan === "Photogrammetry Plan" || plan === "Lidar Plan")
      return lineStrings.length > 0 || !!roundedArea;
    return false;
  };

  const handleResetData = (option) => {
    localStorage.removeItem("edit_project");
    setIsEdit(false);
    try {
      const updatedData = projectData.filter(
        (data) =>
          data.selectedOption !== option &&
          data.subServiceKey !== (selectedSubServiceKey || null)
      );

      setProjectData(updatedData);
      if (option === "Videos Plan") {
        drawRef?.current?.deleteAll();
        drawRef?.current?.changeMode("draw_line_string");
        setLineCoords([]);
        setVideoPlanData([]);
        setVideoPlanLine([]);
      } else if (option === "Photos Plan") {
        setMarkers([]);
      } else if (
        option === planConfig?.ground_control?.ground_control_points_plan
      ) {
        setGroundControlMarkers([]);
      } else {
        drawRef?.current?.deleteAll();
        drawRef?.current?.changeMode("draw_polygon");
        setLineCoords([]);
        setLineStrings([]);
      }

      // Reset step flow if we're in step-based mode
      if (currentStep > 0) {
        setCurrentStep(0);
        setSelectedOption("");
      }
    } catch (error) {
      console.error("Error resetting project data", error);
    }
  };

  const handleResetSubService = (plan, subKey) => {
    try {
      const updatedData = projectData.filter(
        (data) =>
          !(
            data.selectedOption === plan &&
            (data.subServiceKey || "default") === (subKey || "default")
          )
      );
      setProjectData(updatedData);

      if (plan === "Videos Plan") {
        drawRef?.current?.deleteAll();
        drawRef?.current?.changeMode("draw_line_string");
        setLineCoords([]);
        setVideoPlanData([]);
        setVideoPlanLine([]);
      } else if (plan === "Photos Plan") {
        setMarkers([]);
      } else if (
        plan === planConfig?.ground_control?.ground_control_points_plan
      ) {
        setGroundControlMarkers([]);
      } else {
        drawRef?.current?.deleteAll();
        drawRef?.current?.changeMode("draw_polygon");
        setLineCoords([]);
        setLineStrings([]);
      }
    } catch (error) {
      console.error("Error resetting sub-service data", error);
    }
  };

  const handleKmlUpload = (data, type) => {
    try {
      const features = Array.isArray(data?.features) ? data.features : [];

      if (selectedOption === "Photos Plan") {
        setMarkers(features);
      } else if (
        selectedOption ===
        planConfig?.ground_control?.ground_control_points_plan
      ) {
        setGroundControlMarkers(features);
      } else if (selectedOption === "Videos Plan") {
        // Persist imported features to existing video plan states
        setVideoPlanData(features);
        setVideoPlanLine(
          features.filter((f) => f?.geometry?.type === "LineString")
        );
        setLineStrings((prev) => {
          // Replace existing with imported to mirror manual draw semantics
          return [...features];
        });
      } else if (
        selectedOption === "Photogrammetry Plan" ||
        selectedOption === "Lidar Plan"
      ) {
        // Persist polygons to shared lineStrings state and compute area
        const polygonFeatures = features.filter(
          (f) => f?.geometry?.type === "Polygon"
        );
        setLineStrings(polygonFeatures);

        try {
          const fc = { type: "FeatureCollection", features: polygonFeatures };
          const area = turf.area(fc);
          setRoundedArea(area);
        } catch (_e) {
          // ignore area calc error
        }
      }

      // Push imported geometries into Mapbox Draw for immediate visibility
      try {
        if (
          drawRef?.current &&
          (selectedOption === "Videos Plan" ||
            selectedOption === "Photogrammetry Plan" ||
            selectedOption === "Lidar Plan")
        ) {
          // Clear existing draw features and add imported ones
          drawRef.current.deleteAll();
          features.forEach((f) => {
            if (
              f?.geometry?.type === "LineString" ||
              f?.geometry?.type === "Polygon"
            ) {
              try {
                drawRef.current.add(f);
              } catch (_e) {}
            }
          });

          if (selectedOption === "Videos Plan") {
            drawRef.current.changeMode("draw_line_string");
          } else {
            drawRef.current.changeMode("draw_polygon");
          }
          safeResize();
        }
      } catch (_e) {
        // ignore draw add errors
      }

      // Center and fly to imported data
      try {
        const fc = { type: "FeatureCollection", features: features };
        const centerPt = turf.center(fc);
        const center = centerPt?.geometry?.coordinates;
        if (Array.isArray(center) && center.length === 2) {
          mapRef?.current?.getMap()?.flyTo({
            center,
            zoom: data.zoomLevel || 17,
          });
        }
      } catch (_e) {
        // ignore center calc error
      }
    } catch (error) {
      console.error("Error handling KML upload", error);
    }
  };

  // Render the alert dialog
  const AlertDialog = () => {
    if (!showAlert) return null;

    return (
      <Dialog
        open={showAlert}
        onClose={() => setShowAlert(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Save Your Selection</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Your plan has been successfully saved. You can now proceed by
            clicking 'Proceed to quote'.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {allPlansAdded() !== true && (
            <Button
              onClick={() => {
                setShowAlert(false);
              }}
            >
              Add More Services
            </Button>
          )}
          <Button onClick={() => handleSubmitData()} variant="contained">
            Add to Quote
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const mapHeader = {
    checkDesign: {
      whiteSpace: "nowrap",
      padding: "0",
    },
    checkSquare: {
      padding: "0",
      minWidth: "34px",
    },
    mapTopBar: {
      backgroundColor: "#F0F4F9",
      boxShadow: "none",
    },
    mapTopBarIn: {
      display: "flex",
      justifyContent: "flex-start",
      padding: "0 0px",
    },
  };

  const handleOnMapClick = async (event) => {
    if (searchQuery === "") {
      const { lngLat } = event;
      const { lng, lat } = lngLat;

      if (lng && lat) {
        try {
          const res = await axios.get(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json`,
            {
              params: {
                access_token: MAPBOX_TOKEN,
              },
            }
          );
          const data = res?.data;
          const place = data?.features[0];
          setSearchQuery(place?.place_name || "No address found");
          // setAddressSuggestions(data.features);
        } catch (err) {
          console.error("Error fetching location:", err);
        }
      } else {
        setAddressSuggestions([]);
      }
    }
  };

  const handleKmlDownload = async () => {
    setIsKmlDownloading(true);
    const storeData = handleProjectSave();
    let features = [];
    let filename = `${selectedOption}-${Date.now()}`;

    if (selectedOption === "Photos Plan") {
      features = markers;
    } else if (
      selectedOption === planConfig?.ground_control?.ground_control_points_plan
    ) {
      features = groundControlMarkers;
    } else {
      features = storeData?.features || [];
    }

    console.log(">>>storeData>>", storeData);
    console.log(">>>features.length>>", features.length);
    if (features.length) {
      try {
        let res = await downloadKmlApi(IP);
        if (res.status === false) {
          toast.error(res.message);
          setIsKmlDownloading(false);
          return;
        }

        generateKML(selectedOption, filename, features);
        toast.success("Kml file downloaded successfully");
        setIsKmlDownloading(false);
      } catch (error) {
        toast.error(error?.message || "Error download kml");
        setIsKmlDownloading(false);
      }
    } else {
      toast.info("Nothing to download!");
      setIsKmlDownloading(false);
    }
  };

  const handleCreateProject = () => {
    setCurrentStep(1);
  };

  return (
    <Box display="flex" flexDirection="column" height="90vh">
      <AppBar style={mapHeader.mapTopBar} position="static">
        <Toolbar
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            padding: isMobile ? "8px" : "16px",
            gap: isMobile ? "10px" : "20px",
          }}
          // className="searchFilterInner"
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
            }}
          >
            {isMobile && (
              <IconButton
                aria-label="menu"
                onClick={() => setOpenMenu(!openMenu)}
              >
                <MenuIcon />
              </IconButton>
            )}

            <TextField
              className="searchBarHeader"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyPress={(e) => e.key === "Enter" && handleSearchSubmit()}
              placeholder="Enter address or search area"
              variant="outlined"
              size="small"
              fullWidth={isMobile}
              sx={{
                backgroundColor: "white",
                borderRadius: "50px",
                marginLeft: isMobile ? 0 : 2,
                width: isMobile ? "40%" : "40%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  paddingLeft: "10px",
                },
                "& .MuiInputLabel-root": {
                  lineHeight: "normal",
                },
              }}
              InputProps={{
                className: "searchBarHeaderInner",
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#555" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Address Suggestions */}
          {addressSuggestions.length > 0 && (
            <Paper
              sx={{
                position: "absolute",
                top: "100%",
                left: "0",
                right: "0",
                zIndex: 10,
                maxHeight: "200px",
                overflowY: "auto",
                backgroundColor: "white",
              }}
            >
              <MenuList>
                {addressSuggestions.map((address) => (
                  <MenuItem
                    key={address.id}
                    onClick={() => handleAddressSelect(address)}
                  >
                    {address.place_name}
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          )}

          {/* Instruction Alert */}
          {instruction && currentStep === 0 && (
            <Box
              sx={{
                width: isMobile ? "100%" : "auto",
                marginTop: isMobile ? "8px" : "0",
              }}
            >
              <Alert severity="info" variant="filled">
                {instruction}
              </Alert>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Box
        display="flex"
        flexDirection="row"
        flex="1"
        sx={{ padding: "0px", height: "100%", width: "100%" }}
      >
        <MapSidebar
          handleSave={handleSave}
          handleEdit={handleEdit}
          handleOptionChange={handleOptionChange}
          selectedOption={selectedOption}
          isOptionAdded={isOptionAdded}
          isSelectedOption={isSelectedOption}
          isSelectedSubService={isSelectedSubService}
          handleInlineSave={handleInlineSave}
          handleResetSubService={handleResetSubService}
          planHasGeometry={planHasGeometry}
          isActiveSubserviceDirty={isActiveSubserviceDirty}
          handleContinueAll={handleContinueAll}
          handleResetData={handleResetData}
          videoSelectRef={videoSelectRef}
          grammeSelectRef={grammeSelectRef}
          lidarSelectRef={lidarSelectRef}
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
          setOpenKmlDialog={setOpenKmlDialog}
          isReady={isReady}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          setPrName={setPrName}
          prName={prName}
          defaultServiceKey={
            selectedMainServiceKey || planConfig?.projectTypes?.[0]?.key
          }
          selectedMainServiceKey={selectedMainServiceKey}
          selectedSubServiceKey={selectedSubServiceKey}
          isEdit={isEdit}
          setSelectedMainServiceKey={setSelectedMainServiceKey}
          handleSubmitData={handleSubmitData}
          addKml={() => setOpenKmlDialog(true)}
        />
        <Map
          ref={mapRef}
          onClick={handleOnMapClick}
          initialViewState={viewState}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
          mapboxAccessToken={MAPBOX_TOKEN}
          dragPan={interactive} // Enable/disable map dragging
          scrollZoom={interactive} // Enable/disable zooming
          doubleClickZoom={isMobile ? false : true}
          onMove={(evt) => setViewState(evt.viewState)}
        >
          {selectedOption ===
            planConfig?.ground_control?.ground_control_points_plan &&
            groundControlMarkers.map((marker) => {
              const markerId = marker.properties.id;
              return (
                <MarkerComponent
                  key={markerId}
                  marker={marker}
                  updateDirection={updateDirection}
                  deleteMarker={deleteMarker}
                  setInteractive={setInteractive}
                  isEdit={isEdit}
                  isMobile={isMobile}
                  isPhoto={false}
                />
              );
            })}

          {selectedOption === "Photos Plan" &&
            markers.map((marker) => {
              const markerId = marker.properties.id;
              return (
                <MarkerComponent
                  key={markerId}
                  marker={marker}
                  updateDirection={updateDirection}
                  deleteMarker={deleteMarker}
                  setInteractive={setInteractive}
                  isEdit={isEdit}
                  isMobile={isMobile}
                  isPhoto={true}
                />
              );
            })}
          {selectedOption === "Ground Control Points Plan" && (
            <style>
              {`
           .draw-circle-btn{
             top: 106px !important;
           }
          `}
            </style>
          )}
          {selectedOption && selectedOption !== "Photos Plan" && (
            <Tooltip title="circle tool">
              <style>
                {`
      .mapbox-gl-draw_trash {
       border-radius:0px !important;
      }
        .mapboxgl-ctrl-group {
       border-radius:0px !important;
       
       }
    `}
              </style>
              <button
                className="draw-circle-btn"
                onClick={() => {
                  drawRef?.current?.changeMode("draw_circle");
                }}
              >
                <CircleOutlinedIcon />
              </button>
            </Tooltip>
          )}

          {/* 
          {selectedOption === "Photos Plan" && (
            <Tooltip title="Draw your photos plan">
              <button
                className={`draw-photo-btn ${
                  isPhotoDraw && "draw-photo-btn-active"
                }`}
                onClick={() => {
                  let isDraw = localStorage.getItem("photo_draw");
                  if (isDraw) {
                    setIsPhotoDraw(false);
                    localStorage.removeItem("photo_draw");
                    drawRef?.current?.changeMode("simple_select");
                  } else {
                    setIsPhotoDraw(true);
                    localStorage.setItem("photo_draw", true);
                    drawRef?.current?.changeMode("draw_polygon");
                  }
                }}
              >
                <PolylineIcon />
              </button>
            </Tooltip>
          )}
           
          {/* {<RenderFeature />} */}
          <Tooltip title={isKmlDownloading ? "Downloading" : "Download as kml"}>
            <button
              className="download-kml-btn"
              onClick={handleKmlDownload}
              disabled={isKmlDownloading}
            >
              {isKmlDownloading ? (
                <CircularProgress size="20px" />
              ) : (
                <Download />
              )}
            </button>
          </Tooltip>
          <NavigationControl position="bottom-right" />
          {searchLocation && (
            <Marker
              longitude={searchLocation.longitude}
              latitude={searchLocation.latitude}
              anchor="bottom"
            />
          )}

          {currentStep === 0 && !selectedOption && (
            <CustomMarkerMap
              viewState={viewState}
              setViewState={setViewState}
              onCreate={handleCreateProject}
            />
          )}

          <InstructionTooltip
            instruction={editInstruction}
            onClose={() => {
              setEditInstruction({
                message: "",
                isShow: false,
                position: {},
              });
            }}
          />
        </Map>
      </Box>

      <AlertDialog />
      <UploadKMLDialog
        open={openKmlDialog}
        setOpen={setOpenKmlDialog}
        onKMLParsed={handleKmlUpload}
        type={selectedOption}
      />
      <InstructionOverlay
        type={instructionType}
        onClose={() => setInstructionType(null)}
      />
    </Box>
  );
};

export default MapWithSidebar;
